import { Badge, Button, Card, ConfirmDialog, Field, FormError, Input, LoadingState, Status, Textarea, cn, formatMoney, useToast } from '@lawbee/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarCheck, CalendarX2, Clock, Globe, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, ApiError, errorMessage, fieldErrors } from '../../lib/api';
import { formatInTz, todayIn, useParam } from '../../lib/hooks';
import { PublicFrame } from '../../shell/RootPages';
import { ConfidentialityNotice, DoneBlock, InvalidLink, addDays, formatCalendarDate, publicQueryState } from './shared';

type Slot = { startsAt: string; endsAt: string; localDate: string; localTime: string };

type SlotsResponse = {
  type: { name: string; kind?: string; durationMin: number; priceCents: number; description: string | null };
  timezone: string;
  slots: Slot[];
  firm: { name: string; confidentialityNotice: string };
};

type BookResponse = { ok: boolean; bookingId: string; status: 'confirmed' | 'pending_payment' | string; payUrl: string | null; manageUrl: string };

const DAYS = 14;

function tzLabel(tz: string): string {
  const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const abbr = parts.find((p) => p.type === 'timeZoneName')?.value;
    return abbr ? `${city} time (${abbr})` : `${city} time`;
  } catch {
    return tz;
  }
}

function formatWhen(iso: string, tz: string) {
  return {
    date: formatInTz(iso, tz, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
    time: formatInTz(iso, tz, { hour: 'numeric', minute: '2-digit' }),
  };
}

// ---------------------------------------------------------------------------
// Book a consultation
// ---------------------------------------------------------------------------

export function BookingPage() {
  const slug = useParam('slug');
  const typeSlug = useParam('typeSlug');
  const toast = useToast();
  // The browser's today is a fine start; the API clamps to the firm's today.
  const [from] = useState(() => todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone));
  const to = addDays(from, DAYS - 1);
  const base = `/api/public/booking/${encodeURIComponent(slug)}/${encodeURIComponent(typeSlug)}`;

  const q = useQuery({
    queryKey: ['public', 'booking', slug, typeSlug, from],
    queryFn: () => api.get<SlotsResponse>(`${base}/slots?from=${from}&to=${to}`),
    retry: false,
  });

  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rootError, setRootError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const byDate = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of q.data?.slots ?? []) {
      const list = m.get(s.localDate) ?? [];
      list.push(s);
      m.set(s.localDate, list);
    }
    return m;
  }, [q.data]);

  const days = useMemo(() => {
    const start = q.data ? todayIn(q.data.timezone) : from;
    return Array.from({ length: DAYS }, (_, i) => addDays(start, i));
  }, [q.data, from]);

  // Default to the first day with availability; drop a selection that no longer exists.
  useEffect(() => {
    if (!q.data) return;
    if (date && byDate.has(date)) return;
    setDate(days.find((d) => byDate.has(d)) ?? null);
  }, [q.data, byDate, days, date]);

  useEffect(() => {
    if (slot && !(q.data?.slots ?? []).some((s) => s.startsAt === slot.startsAt)) setSlot(null);
  }, [q.data, slot]);

  const book = useMutation({
    mutationFn: (startsAt: string) =>
      api.post<BookResponse>(base, {
        startsAt,
        name: form.name.trim(),
        email: form.email.trim(),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      }),
    onSuccess: (res) => {
      if (res.payUrl) {
        setRedirecting(true);
        window.location.assign(res.payUrl);
        return;
      }
      window.scrollTo({ top: 0 });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error('That time was just taken', 'Please choose another time.');
        setSlot(null);
        void q.refetch();
        return;
      }
      const f = fieldErrors(err);
      setErrors(f);
      setRootError(Object.keys(f).length > 0 ? 'Please check the highlighted fields.' : errorMessage(err));
    },
  });

  const state = publicQueryState(q, {
    loadingLabel: 'Finding available times',
    notFound: <InvalidLink title="This booking page is not available" description="The link may be out of date. Please contact the firm directly." />,
  });
  if (state) return state;
  const data = q.data!;
  const tz = data.timezone;

  if (redirecting) {
    return (
      <PublicFrame firmName={data.firm.name}>
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
          <p className="text-[15px] font-medium">Redirecting to secure payment</p>
          <p className="text-sm text-muted-foreground">Your time is held while you complete the payment.</p>
        </div>
      </PublicFrame>
    );
  }

  if (book.data) {
    const when = book.variables ? formatWhen(book.variables, tz) : null;
    return (
      <PublicFrame firmName={data.firm.name}>
        <DoneBlock
          icon={<CalendarCheck />}
          title="Your consultation is booked"
          action={
            <Button asChild variant="outline">
              <a href={book.data.manageUrl}>View or cancel your booking</a>
            </Button>
          }
        >
          <p className="font-medium text-foreground">{data.type.name}</p>
          {when ? (
            <p className="figures text-foreground">
              {when.date}, {when.time}
            </p>
          ) : null}
          <p className="text-sm">Times are in {tzLabel(tz)}. A confirmation has been sent to {form.email.trim()}.</p>
        </DoneBlock>
      </PublicFrame>
    );
  }

  const daySlots = date ? (byDate.get(date) ?? []) : [];
  const hasAny = byDate.size > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setRootError(null);
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Enter a valid email address.';
    setErrors(errs);
    if (!slot || Object.keys(errs).length > 0) return;
    book.mutate(slot.startsAt);
  };

  return (
    <PublicFrame firmName={data.firm.name}>
      <div className="space-y-6">
        <Card className="p-5 sm:p-6">
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.025em]">{data.type.name}</h1>
          {data.type.description ? <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-muted-foreground">{data.type.description}</p> : null}
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-4 text-muted-foreground" aria-hidden />
              <span className="figures">{data.type.durationMin} min</span>
            </span>
            {data.type.priceCents > 0 ? (
              <span className="figures font-medium">{formatMoney(data.type.priceCents)}</span>
            ) : (
              <Badge variant="success">Free</Badge>
            )}
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Globe className="size-4" aria-hidden /> Times shown in {tzLabel(tz)}
            </span>
          </div>
          {data.type.priceCents > 0 ? <p className="mt-3 text-xs text-muted-foreground">You pay securely through Stripe when you book.</p> : null}
        </Card>

        <section aria-labelledby="date-heading" className="space-y-3">
          <h2 id="date-heading" className="text-[15px] font-semibold">
            Choose a day
          </h2>
          <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0" role="listbox" aria-label="Available days">
            <div className="flex gap-2">
              {days.map((d) => {
                const available = byDate.has(d);
                const selected = d === date;
                return (
                  <button
                    key={d}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={!available}
                    onClick={() => {
                      setDate(d);
                      setSlot(null);
                    }}
                    className={cn(
                      'flex w-[60px] shrink-0 flex-col items-center gap-0.5 rounded-xl py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/25',
                      selected ? 'bg-primary text-primary-foreground' : 'bg-card shadow-card hover:bg-muted',
                      !available && 'cursor-not-allowed opacity-40 shadow-none hover:bg-card',
                    )}
                  >
                    <span className={cn('text-[11px] font-medium uppercase tracking-wide', selected ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                      {formatCalendarDate(d, { weekday: 'short' })}
                    </span>
                    <span className="figures text-[17px] font-semibold">{formatCalendarDate(d, { day: 'numeric' })}</span>
                    <span className={cn('text-[11px]', selected ? 'text-primary-foreground/80' : 'text-muted-foreground')}>{formatCalendarDate(d, { month: 'short' })}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section aria-labelledby="time-heading" className="space-y-3">
          <h2 id="time-heading" className="text-[15px] font-semibold">
            {date ? `Times on ${formatCalendarDate(date, { weekday: 'long', month: 'long', day: 'numeric' })}` : 'Choose a time'}
          </h2>
          {!hasAny ? (
            <div className="flex flex-col items-center gap-2 rounded-xl bg-muted/50 px-4 py-8 text-center">
              <CalendarX2 className="size-6 text-muted-foreground" aria-hidden />
              <p className="text-[15px] font-medium">No times are available in the next two weeks</p>
              <p className="text-sm text-muted-foreground">Please check back later or contact {data.firm.name} directly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {daySlots.map((s) => {
                const selected = slot?.startsAt === s.startsAt;
                return (
                  <button
                    key={s.startsAt}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSlot(s)}
                    className={cn(
                      'figures h-11 rounded-[10px] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/25',
                      selected ? 'bg-primary text-primary-foreground' : 'bg-card shadow-[inset_0_0_0_1px_var(--color-input)] hover:bg-muted',
                    )}
                  >
                    {s.localTime}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {slot ? (
          <section aria-labelledby="details-heading" className="rounded-2xl bg-card p-5 shadow-card sm:p-7">
            <h2 id="details-heading" className="text-[15px] font-semibold">
              Your details
            </h2>
            <p className="figures mt-1 text-sm text-muted-foreground">
              {formatWhen(slot.startsAt, tz).date}, {slot.localTime}
            </p>
            <form noValidate onSubmit={submit} className="mt-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" htmlFor="bk-name" error={errors.name} required>
                  <Input id="bk-name" autoComplete="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} aria-invalid={errors.name ? true : undefined} />
                </Field>
                <Field label="Email" htmlFor="bk-email" error={errors.email} required>
                  <Input
                    id="bk-email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    aria-invalid={errors.email ? true : undefined}
                  />
                </Field>
              </div>
              <Field label="Phone" htmlFor="bk-phone" error={errors.phone}>
                <Input id="bk-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </Field>
              <Field label="Anything the lawyer should know beforehand?" htmlFor="bk-notes" error={errors.notes} description="Briefly. Please do not include highly sensitive details.">
                <Textarea id="bk-notes" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </Field>
              <FormError message={rootError} />
              <div className="flex justify-end">
                <Button type="submit" size="lg" loading={book.isPending} className="w-full sm:w-auto">
                  {data.type.priceCents > 0 ? `Continue to payment (${formatMoney(data.type.priceCents)})` : 'Confirm booking'}
                </Button>
              </div>
            </form>
          </section>
        ) : null}

        <ConfidentialityNotice notice={data.firm.confidentialityNotice} />
      </div>
    </PublicFrame>
  );
}

// ---------------------------------------------------------------------------
// Manage a booking
// ---------------------------------------------------------------------------

type ManageResponse = {
  booking: { typeName: string; name?: string; startsAt: string; endsAt: string; status: string };
  firm: { name: string; phone?: string | null };
  timezone: string;
};

const BOOKING_STATUS: Record<string, { tone: 'done' | 'progress' | 'risk' | 'blocked' | 'idle'; label: string }> = {
  confirmed: { tone: 'done', label: 'Confirmed' },
  pending_payment: { tone: 'risk', label: 'Waiting for payment' },
  cancelled: { tone: 'blocked', label: 'Cancelled' },
  completed: { tone: 'done', label: 'Completed' },
  no_show: { tone: 'idle', label: 'Missed' },
};

export function ManageBookingPage() {
  const token = useParam('token');
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const path = `/api/public/booking/manage/${encodeURIComponent(token)}`;
  const q = useQuery({ queryKey: ['public', 'booking-manage', token], queryFn: () => api.get<ManageResponse>(path), retry: false });
  const cancel = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`${path}/cancel`),
    onSuccess: async () => {
      setConfirming(false);
      toast.success('Your booking is cancelled');
      await q.refetch();
    },
    onError: (err) => toast.error('Could not cancel', errorMessage(err)),
  });

  const state = publicQueryState(q, { loadingLabel: 'Loading your booking' });
  if (state) return state;
  if (!q.data) return <LoadingState />;
  const { booking, firm, timezone } = q.data;
  const status = BOOKING_STATUS[booking.status] ?? { tone: 'idle' as const, label: booking.status.replace(/_/g, ' ') };
  const start = formatWhen(booking.startsAt, timezone);
  const end = formatInTz(booking.endsAt, timezone, { hour: 'numeric', minute: '2-digit' });
  const upcoming = new Date(booking.startsAt).getTime() > Date.now();
  const cancellable = upcoming && (booking.status === 'confirmed' || booking.status === 'pending_payment');
  const cancelled = booking.status === 'cancelled';

  return (
    <PublicFrame firmName={firm.name}>
      <div className="space-y-6">
        <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.025em]">Your booking</h1>
        <Card className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[17px] font-semibold">{booking.typeName}</p>
              <p className="text-sm text-muted-foreground">with {firm.name}</p>
            </div>
            <Status tone={status.tone}>{status.label}</Status>
          </div>
          <dl className={cn('mt-5 grid gap-3 text-sm sm:grid-cols-2', cancelled && 'text-muted-foreground line-through')}>
            <div>
              <dt className="text-xs text-muted-foreground">Date</dt>
              <dd className="figures mt-0.5 font-medium">{start.date}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Time</dt>
              <dd className="figures mt-0.5 font-medium">
                {start.time} to {end}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">Times are in {tzLabel(timezone)}.</p>
        </Card>

        {cancelled ? (
          <p className="text-[15px] text-muted-foreground">
            This booking is cancelled. If you would like a new time, please book again or contact {firm.name}
            {firm.phone ? (
              <>
                {' '}
                at <a href={`tel:${firm.phone}`} className="figures font-medium text-foreground">{firm.phone}</a>
              </>
            ) : null}
            .
          </p>
        ) : cancellable ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">Cannot make it? Cancel so the time can go to someone else.</p>
            <Button variant="outline" onClick={() => setConfirming(true)}>
              Cancel booking
            </Button>
          </div>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Cancel this booking?"
        description={`${booking.typeName} on ${start.date} at ${start.time}. If you paid a consultation fee, contact the firm about a refund.`}
        confirmLabel="Cancel booking"
        destructive
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
      />
    </PublicFrame>
  );
}
